/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-11-06 11:41:58                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2024-11-26 17:04:12                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Components
  * Services
  * Styles
  * useEffect: get quotation data
  * useEffect: Set breadcrumb path 
  * Quotation: quotation view
*/

/* Imports */
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, Typography } from "@material-tailwind/react";
/***/

/* Components */
import Loading from "../../components/loading/Loading";
import { Breadcrumb } from "../../components/breadcrumb/Breadcrumb";
import TechnicianDisplay from "../../components/data/technician/TechnicianDisplay";
import CustomerDisplay from "../../components/data/customer/CustomerDisplay";
import ContactDisplay from "../../components/data/contact/ContactDisplay";
import DateDisplay from "../../components/data/date/DateDisplay";
import { Table } from "../../components/table/Table";
/***/

/* Services */
import { api } from "../../services/api/api";
import { formatQuotationAmount, formatQuotationDuration } from "./quotationLabor";
/***/

/* Styles */
import "./Quotation.scss";
/***/

/**
* Quotation: quotation view
* display quotation details 
*/
export function Quotation() {
  const {id} = useParams();
  const [quotation, setQuotation] = useState<EasyTech.Quotation>();
  const [path, setPath] = useState<{name: string, path: string}[]>([]);

  /**
  * useEffect: get quotation data
  * reload on id change 
  */
  useEffect(() => {
    api.get(`users/quotations/${id}`)
      .then((ret) => setQuotation(ret as EasyTech.Quotation))
      .catch((err) => console.error(err));
  }, [id]);
  /***/

  /**
  * useEffect: Set breadcrumb path 
  * reload on quoation data change
  */
  useEffect(() => {
    if (quotation) {
      setPath([{
        name: "quotations",
        path: "/quotations"
      }, {
        name: quotation.ref,
        path: `/quotations/${quotation.id}`
      }]);
    }
  }, [quotation]);
  /***/

  const services = quotation?.services ?? [];

  return (
    <div className="quotation-details">
      {quotation ?
        <>
          <Breadcrumb path={path}/>

          <Typography variant="h2">
            {quotation.ref}
          </Typography>

          <Typography variant="h4" className="sub-title">
            {t("Informations")}
          </Typography>

          <Card className="details-card">
            <div className="field">
              <label>{t("common:ref:label")}</label>
              <Typography>{quotation.ref}</Typography>
            </div>

            <div className="field">
              <label>{t("common:date:label")}</label>
              <DateDisplay date={new Date(quotation.date)} />
            </div>

            <div className="field">
              <label>{t("common:technician")}</label>
              <TechnicianDisplay technician={quotation.technician} />
            </div>

            <div className="field">
              <label>{t("common:customer")}</label>
              <CustomerDisplay customer={quotation.customer} />
            </div>

            <div className="field">
              <label>{t("common:contact")}</label>
              <ContactDisplay contact={quotation.contact} />
            </div>

            <div className="field">
              <label>{t("common:duration")}</label>
              <Typography>
                {formatQuotationDuration(
                  quotation.duration,
                  quotation.pricing?.techBasePrice,
                  quotation.pricing?.techFees
                )}
              </Typography>
            </div>

            <div className="field">
              <label>{t("quotation:techBasePrice")}</label>
              <Typography>{formatQuotationAmount(quotation.pricing?.techBasePrice)}</Typography>
            </div>

            <div className="field">
              <label>{t("quotation:techFees")}</label>
              <Typography>{formatQuotationAmount(quotation.pricing?.techFees)}</Typography>
            </div>

            <div className="field">
              <label>{t("quotation:estimatedCost")}</label>
              <Typography>{formatQuotationAmount(quotation.pricing?.estimatedCost)}</Typography>
            </div>
          </Card>

          {quotation.parts.length > 0 &&
            <>
              <Typography variant="h4" className="sub-title">
                {t("piece:title")}
              </Typography>

              <Card>
                <Table tableHead={[{label: t("common:ref:label"), orderKey: "ref"}, {label: t("common:label"), orderKey: "label"}, {label: t("piece:form:fields:quantity:label"), orderKey: "quantity"}, {label: t("common:price"), orderKey: "price"}]}
                enableRowLinks={true}>
                  {quotation.parts.map((el, i) => {
                    const isLast = i === quotation.parts.length - 1;
                    const classes = isLast ? "p-2" : "p-2 border-b border-blue-gray-50";
                    const partUrl = `/parts/${el.part.id}`;
                    
                    return (
                      <tr key={`ref-${i}-${el.part.ref}`} data-href={partUrl}>
                        <td className={classes}>
                          <Typography variant="small">
                            {el.part.ref}
                          </Typography>
                        </td>

                        <td className={classes}>
                          <Typography variant="small">
                            {el.part.label}
                          </Typography>
                        </td>

                        <td className={classes}>
                          <Typography variant="small">
                            {el.quantity}
                          </Typography>
                        </td>

                        <td className={classes}>
                          <Typography variant="small">
                            {el.part.price}
                          </Typography>
                        </td>
                      </tr>
                    );
                  })}
                </Table>
              </Card>
            </>
          }

          {services.length > 0 &&
            <>
              <Typography variant="h4" className="sub-title">
                {t("quotation:services")}
              </Typography>

              <Card>
                <Table tableHead={[{label: t("common:name"), orderKey: "name"}, {label: t("common:price"), orderKey: "price"}]}>
                  {services.map((service, i) => {
                    const isLast = i === services.length - 1;
                    const classes = isLast ? "p-2" : "p-2 border-b border-blue-gray-50";

                    return (
                      <tr key={`service-${i}-${service.name}`}>
                        <td className={classes}>
                          <Typography variant="small">
                            {service.name}
                          </Typography>
                        </td>

                        <td className={classes}>
                          <Typography variant="small">
                            {service.price}
                          </Typography>
                        </td>
                      </tr>
                    );
                  })}
                </Table>
              </Card>
            </>
          }
        </> 
      : <Loading/>}
    </div>
  );
}
/***/
